import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { AppShell } from "@/components/app-shell";
import { requireRole } from "@/lib/session";
import { getMistakeGroups, type MistakeGroup } from "@/lib/mistake-review.functions";
import { AlertCircle, ArrowRight, Sparkles } from "lucide-react";

export const Route = createFileRoute("/learner/mistakes")({
  ssr: false,
  beforeLoad: () => requireRole("learner"),
  head: () => ({
    meta: [
      { title: "Mistake Review — MAKAI" },
      {
        name: "description",
        content:
          "Group your recent mistakes by topic and see what to practise next — no long lists of wrong answers.",
      },
      { property: "og:title", content: "Mistake Review — MAKAI" },
      {
        property: "og:description",
        content:
          "MAKAI groups your recent mistakes by topic and error type so you can focus your practice.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: MistakesPage,
});

const KIND_LABEL: Record<string, string> = {
  arithmetic_error: "Calculation slip",
  concept_error: "Understanding gap",
  method_error: "Wrong method",
  unit_error: "Missing units",
  transcription_error: "Copy-down error",
  careless: "Careless slip",
  unknown: "Recent mistake",
};

function kindLabel(kind: string) {
  return KIND_LABEL[kind] ?? kind.replace(/_/g, " ");
}

function MistakesPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["mistake-groups", 14],
    queryFn: () => getMistakeGroups({ data: { days: 14 } }),
  });

  const groups = (data ?? []) as MistakeGroup[];

  return (
    <AppShell
      role="learner"
      title="Mistake review"
      subtitle="Grouped by topic — practise once, fix many."
    >
      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading your recent work…</div>
      ) : groups.length === 0 ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-center shadow-soft">
          <Sparkles className="mx-auto h-6 w-6 text-primary" />
          <p className="mt-2 text-sm text-muted-foreground">
            No recent mistakes to review. Keep going — new practice will appear here when needed.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <div
              key={g.key}
              className="rounded-2xl border border-border bg-card p-4 shadow-soft"
            >
              <div className="flex items-start gap-3">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-destructive/10 text-destructive">
                  <AlertCircle className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="truncate font-medium">{g.node_label}</div>
                    {g.subject ? (
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                        {g.subject}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {kindLabel(g.error_kind)} · {g.count} recent
                    {g.count === 1 ? " time" : " times"}
                  </div>
                </div>
                <Link
                  to="/learner/coach"
                  className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground"
                >
                  Practise <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
